import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";

export interface DropdownOption<T extends string> {
  value: T;
  label: string;
}

export interface DropdownProps<T extends string> {
  label: string;
  value: T;
  options: DropdownOption<T>[];
  onChange: (value: T) => void;
  size?: "sm" | "md";
  align?: "left" | "right";
  className?: string;
  disabled?: boolean;
}

const SIZE: Record<
  NonNullable<DropdownProps<string>["size"]>,
  { trigger: string; option: string }
> = {
  sm: {
    trigger: "px-2.5 py-1.5 text-xs",
    option: "px-2.5 py-1.5 text-xs",
  },
  md: {
    trigger: "px-3 py-1.5 text-sm",
    option: "px-3 py-2 text-sm",
  },
};

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 12 12"
      aria-hidden="true"
      className={[
        "h-3 w-3 shrink-0 text-accent transition-transform",
        open ? "rotate-180" : "",
      ].join(" ")}
    >
      <path
        d="M2.2 4.2 6 8l3.8-3.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Dropdown<T extends string>({
  label,
  value,
  options,
  onChange,
  size = "md",
  align = "left",
  className,
  disabled = false,
}: DropdownProps<T>) {
  const reactId = useId();
  const listId = `${reactId}-list`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const [activeIndex, setActiveIndex] = useState(selectedIndex);

  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? options[0],
    [options, value],
  );

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const gap = 6;
    const estimatedHeight = Math.min(options.length * 36 + 8, 256);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < estimatedHeight && rect.top > spaceBelow;
    const style: CSSProperties = {
      position: "fixed",
      minWidth: rect.width,
      maxWidth: "20rem",
      zIndex: 80,
    };
    if (openUp) {
      style.bottom = window.innerHeight - rect.top + gap;
    } else {
      style.top = rect.bottom + gap;
    }
    if (align === "right") {
      style.right = window.innerWidth - rect.right;
    } else {
      style.left = rect.left;
    }
    setMenuStyle(style);
  }, [align, options.length]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (open) setActiveIndex(selectedIndex);
  }, [open, selectedIndex]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const commit = (index: number) => {
    const next = options[index];
    if (!next) return;
    onChange(next.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIndex((current) => {
        const delta = event.key === "ArrowDown" ? 1 : -1;
        return (current + delta + options.length) % options.length;
      });
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      commit(activeIndex);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key === "Home" && open) {
      event.preventDefault();
      setActiveIndex(0);
    }
    if (event.key === "End" && open) {
      event.preventDefault();
      setActiveIndex(options.length - 1);
    }
  };

  const sizing = SIZE[size];

  return (
    <div className={["relative inline-flex min-w-0", className ?? ""].join(" ")}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? `${listId}-opt-${activeIndex}` : undefined}
        role="combobox"
        onClick={() => !disabled && setOpen((current) => !current)}
        onKeyDown={onTriggerKeyDown}
        className={[
          "inline-flex min-w-0 max-w-full items-center gap-2 rounded-lg border bg-surface/80 font-medium text-ink outline-none transition",
          sizing.trigger,
          disabled
            ? "cursor-not-allowed opacity-35"
            : "hover:border-accent hover:bg-accent/10",
          open
            ? "border-accent bg-accent/10 shadow-glow"
            : "border-line/35 focus:border-accent",
        ].join(" ")}
      >
        <span className="min-w-0 truncate">{selected?.label ?? "—"}</span>
        <Chevron open={open} />
      </button>

      {open
        ? createPortal(
            <ul
              ref={menuRef}
              id={listId}
              role="listbox"
              aria-label={label}
              style={menuStyle}
              className="max-h-64 overflow-y-auto rounded-xl border border-line/30 bg-surface py-1 shadow-glow backdrop-blur-md"
            >
              {options.map((option, index) => {
                const isSelected = option.value === value;
                const isActive = index === activeIndex;
                return (
                  <li
                    key={option.value}
                    id={`${listId}-opt-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => commit(index)}
                    className={[
                      "flex cursor-pointer items-center justify-between gap-3 font-medium transition",
                      sizing.option,
                      isSelected
                        ? "bg-accent/20 text-accent"
                        : isActive
                          ? "bg-accent/10 text-ink"
                          : "text-muted hover:bg-accent/10 hover:text-ink",
                    ].join(" ")}
                  >
                    <span className="min-w-0 truncate">{option.label}</span>
                    {isSelected ? (
                      <span aria-hidden="true" className="text-accent">
                        ✓
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
